import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Client-side weekly check-in form.
 *
 * Renders the fixed metrics (energy, sleep, adherence, weight, general notes)
 * plus any custom prompts the coach has configured on their profile. The
 * per-question answers ship in `answers: {prompt: text}` so the coach sees
 * them mapped back on the ClientDetail check-in list.
 */
export default function CheckinForm({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [energy, setEnergy] = useState(4);
  const [sleep, setSleep] = useState(7.5);
  const [adherence, setAdherence] = useState(85);
  const [weight, setWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [coachQs, setCoachQs] = useState([]);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!open) return;
    api
      .get("/checkins/questions")
      .then((r) => setCoachQs(r.data?.questions || []))
      .catch(() => setCoachQs([]));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/checkins", {
        energy: Number(energy),
        sleep_hours: Number(sleep) || null,
        adherence_pct: Number(adherence),
        weight_kg: weight ? Number(weight) : null,
        notes: notes || null,
        photo_ids: [],
        answers: Object.keys(answers).length ? answers : null,
      });
      toast.success("Check-in sent to your coach");
      setNotes("");
      setAnswers({});
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

  if (!open) {
    return (
      <button
        data-testid="checkin-open"
        onClick={() => setOpen(true)}
        className="ghost-btn px-4 py-2 text-xs inline-flex items-center gap-2"
      >
        <Activity className="h-3 w-3" /> Weekly check-in
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="obs-card space-y-4" data-testid="checkin-form">
      <div className="flex items-center gap-3">
        <Activity className="h-5 w-5 text-blaze" />
        <h3 className="font-heading text-2xl uppercase">Weekly check-in</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block">
          <div className="overline mb-1">Energy</div>
          <select
            data-testid="checkin-energy"
            value={energy}
            onChange={(e) => setEnergy(e.target.value)}
            className={inputCls}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}/5
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <div className="overline mb-1">Sleep (hrs)</div>
          <input
            type="number"
            step="0.1"
            value={sleep}
            onChange={(e) => setSleep(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <div className="overline mb-1">Adherence %</div>
          <input
            type="number"
            min={0}
            max={100}
            value={adherence}
            onChange={(e) => setAdherence(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <div className="overline mb-1">Weight (kg)</div>
          <input
            type="number"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="optional"
            className={inputCls}
          />
        </label>
      </div>

      {coachQs.length > 0 && (
        <div className="space-y-3 border-t border-obsidian-600 pt-4" data-testid="checkin-coach-qs">
          <div className="overline text-blaze">Questions from your coach</div>
          {coachQs.map((q, i) => (
            <label key={i} className="block">
              <div className="text-xs text-zinc-300 mb-1.5">{q}</div>
              <textarea
                rows={2}
                value={answers[q] || ""}
                onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })}
                className={inputCls}
                data-testid={`checkin-answer-${i}`}
              />
            </label>
          ))}
        </div>
      )}

      <label className="block">
        <div className="overline mb-1">Anything else for your coach</div>
        <textarea
          data-testid="checkin-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What went well · what's hard · what you need"
          className={inputCls}
        />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="ghost-btn px-4 py-2 text-xs">
          Cancel
        </button>
        <button
          data-testid="checkin-submit"
          disabled={saving}
          type="submit"
          className="blaze-btn px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Send className="h-3 w-3" /> {saving ? "Sending..." : "Send check-in"}
        </button>
      </div>
    </form>
  );
}
